import React from 'react';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab, { tabClasses } from '@mui/joy/Tab';
import TabPanel from '@mui/joy/TabPanel';
import {OrdersGeneral} from "./OrdersGeneral";
import {OrdersAnalytics} from "./analytics/OrdersAnalytics";
import {getOrdersTabsFromHash, ORDERS_ANALYTICS_TAB_SLUGS, ORDERS_TOP_SLUGS, OrdersTabs} from "./analytics/slugs";
import {parseOrdersDrillDown, parseStatusParam} from "./analytics/drilldown";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import {useSearchParams, useNavigate, useLocation} from "react-router-dom";

export function Orders() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const bidId = Number(searchParams.get('bidId')) || undefined;
    const bidderId = BigInt(searchParams.get('bidderId') || '') || undefined;
    const status = parseStatusParam(searchParams.get('status'));

    const drillDown = parseOrdersDrillDown(searchParams);
    const hasAnalytics = (bidId === undefined && bidderId === undefined && status === undefined
        && Object.keys(drillDown).length === 0);

    const [tabs, setTabs] = React.useState<OrdersTabs>({topIndex: 0, analyticsIndex: 0});

    React.useEffect(() => {
        setTabs((prev) => getOrdersTabsFromHash(location.hash, searchParams.get('tab') || '', hasAnalytics, prev));
        // if analytics not available but hash points to it, normalize to general
        if (!hasAnalytics && (location.hash || '').replace('#','').toLowerCase().startsWith(ORDERS_TOP_SLUGS[1])) {
            navigate(`#general`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash, location.search, hasAnalytics]);

    const handleTabChange = (_: React.SyntheticEvent | null, value: number | string | null) => {
        const nextIndex = typeof value === 'number' ? value : 0;
        const slug = nextIndex === 1
            ? ORDERS_ANALYTICS_TAB_SLUGS[tabs.analyticsIndex] || ORDERS_ANALYTICS_TAB_SLUGS[0]
            : ORDERS_TOP_SLUGS[0];
        setTabs((prev) => ({...prev, topIndex: nextIndex}));
        navigate(`#${slug}`, { replace: false });
    };

    const handleAnalyticsTabChange = (index: number) => {
        setTabs({topIndex: 1, analyticsIndex: index});
        navigate(`#${ORDERS_ANALYTICS_TAB_SLUGS[index] || ORDERS_ANALYTICS_TAB_SLUGS[0]}`, { replace: false });
    };

    return (
        <React.Fragment>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: {
                        xs: 'calc(12px + var(--Header-height))',
                        sm: 'calc(12px + var(--Header-height))',
                        md: 3,
                    },
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    height: '100dvh',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Ордера
                    </Typography>
                </Box>
                <Tabs value={tabs.topIndex} onChange={handleTabChange} sx={{bgcolor: 'transparent'}}>
                    <TabList
                        tabFlex={1}
                        size="sm"
                        sx={{
                            pl: {xs: 0, md: 4},
                            justifyContent: 'left',
                            [`&& .${tabClasses.root}`]: {
                                fontWeight: '600',
                                flex: 'initial',
                                color: 'text.tertiary',
                                [`&.${tabClasses.selected}`]: {
                                    bgcolor: 'transparent',
                                    color: 'text.primary',
                                    '&::after': {
                                        height: '2px',
                                        bgcolor: 'primary.500',
                                    },
                                },
                            },
                        }}
                    >
                        <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={0}>
                            Главная
                        </Tab>
                        {hasAnalytics
                        ? <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={1}>
                            Аналитика
                        </Tab>
                        : <></>}
                    </TabList>
                    <TabPanel value={0}>
                        <OrdersGeneral key={location.search} bidId={bidId} bidderId={bidderId} status={status}
                                       failReason={drillDown.failReason} comment={drillDown.comment}
                                       gameId={drillDown.gameId} from={drillDown.from} to={drillDown.to}/>
                    </TabPanel>
                    <TabPanel value={1}>
                        <OrdersAnalytics
                            isActive={tabs.topIndex === 1}
                            currentTabIndex={tabs.analyticsIndex}
                            onTabChange={handleAnalyticsTabChange}
                        />
                    </TabPanel>
                </Tabs>
            </Box>

        </React.Fragment>
    );
}
